import { FunctionComponent, MutableRefObject, useEffect } from 'react';
import l, { langKeyExists } from '../../../../lang';
import Table from '../../../common/table';
import RadioButton from '../../../common/radio-button';
import Loading from '../../../common/loading';
import { Urls } from '../../../../config/urls';
import { useSettlements } from '../../../../store/settlements';
import { useHome } from '../../../../store/home';
import { isUsdSettlementBankAccount } from '../../../../utils/bank-account-utils';

import './styles.scss';

interface SelectBeneficiaryProps {
  disabled?: boolean;
  value?: string;
  errorReference?: MutableRefObject<null>;
  externalError?: string;
  onSelect: (value: string) => void;
}

const SelectBeneficiary: FunctionComponent<SelectBeneficiaryProps> = ({
  disabled,
  value = '',
  onSelect,
  errorReference,
  externalError,
}) => {
  const {
    beneficiaries: { loading, data },
    getBeneficiaries,
  } = useSettlements();
  const { balances } = useHome();
  const usdSettlementsAllowed = balances.data?.usdSettlementsAllowed ?? false;

  useEffect(() => {
    getBeneficiaries();
  }, []);

  useEffect(() => {
    if (!value || usdSettlementsAllowed || !data?.length) {
      return;
    }

    const selectedAccount = data.find(account => `${account.id}` === value);
    if (selectedAccount && isUsdSettlementBankAccount(selectedAccount)) {
      onSelect('');
    }
  }, [value, data, usdSettlementsAllowed, onSelect]);

  const formatCurrency = (currency?: string) => {
    if (!currency) {
      return '-';
    }
    return langKeyExists(`common.currency.${currency}`) ? l(`common.currency.${currency}`) : currency;
  };

  const isAccountDisabled = (row: { currency?: string; country: string }) =>
    disabled || (!usdSettlementsAllowed && isUsdSettlementBankAccount(row));

  return (
    <div className="select-beneficiary">
      {loading && <Loading fullScreen />}
      <div className="select-beneficiary__label">
        {data?.length === 0 ? (
          <div>{l('selectBeneficiary.withoutData.title')}</div>
        ) : (
          <div>{l('selectBeneficiary.titleWithData')}</div>
        )}
      </div>
      {data?.length > 0 && (
        <div className="select-beneficiary__table-container">
          <div className="select-beneficiary__table-label">{l('selectBeneficiary.title')}</div>
          <div className="select-beneficiary__table">
            <Table
              idxHiddenColumnsOnMobile={[1, 2, 3, 4]}
              maxHeight="230px"
              headers={[
                '',
                l('bankAccounts.country'),
                l('bankAccounts.name'),
                l('bankAccounts.documentType'),
                l('bankAccounts.document'),
                l('bankAccounts.bankName'),
                l('bankAccounts.bankAccount'),
                l('bankAccounts.currency'),
              ]}
              body={
                data?.map((row, idx) => {
                  const accountDisabled = isAccountDisabled(row);
                  const item = [
                    <RadioButton
                      disabled={accountDisabled}
                      name={`rb-beneficiary-${idx.toString()}`}
                      checked={value === `${row.id}`}
                      value={`${row.id}`}
                      onChange={() => {
                        if (!accountDisabled) {
                          onSelect(`${row.id}`);
                        }
                      }}
                    />,
                    <img
                      alt={`img-country-${row.country}`}
                      className="bank-accounts__country"
                      src={`${Urls.flags.replace(':code', row.country)}`}
                    />,
                    <span>{`${row.beneficiaryFirstName || ''} ${row.beneficiaryLastName || ''}`.trim()}</span>,
                    <span>{row.beneficiaryDocumentType}</span>,
                    <span>{row.beneficiaryDocument}</span>,
                    <span>{row.bankName}</span>,
                    <span>{row.bankAccount}</span>,
                    <span className={accountDisabled && isUsdSettlementBankAccount(row) ? 'select-beneficiary__disabled-currency' : undefined}>
                      {formatCurrency(row.currency)}
                    </span>,
                  ];
                  return item;
                }) || null
              }
              emptyBody={undefined}
            />
          </div>
        </div>
      )}
      <div ref={errorReference} className="select-beneficiary__error">
        {externalError || <div />}
      </div>
    </div>
  );
};

export default SelectBeneficiary;
